import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkBidManagement = ({ onBulkUpdate, onTemplateCreate }) => {
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const buyerProfiles = [
    {
      id: 'profile_1',
      name: 'Auto Insurance - Premium',
      type: 'Auto Insurance',
      status: 'active',
      currentBid: 45.50,
      dailyBudget: 2500,
      winRate: 34.2,
      leadsToday: 23
    },
    {
      id: 'profile_2',
      name: 'Home Insurance - Standard',
      type: 'Home Insurance',
      status: 'active',
      currentBid: 32.75,
      dailyBudget: 1800,
      winRate: 28.7,
      leadsToday: 18
    },
    {
      id: 'profile_3',
      name: 'Life Insurance - High Value',
      type: 'Life Insurance',
      status: 'paused',
      currentBid: 65.00,
      dailyBudget: 3200,
      winRate: 41.3,
      leadsToday: 0
    },
    {
      id: 'profile_4',
      name: 'Auto Insurance - Budget',
      type: 'Auto Insurance',
      status: 'active',
      currentBid: 28.25,
      dailyBudget: 1200,
      winRate: 22.1,
      leadsToday: 31
    }
  ];

  const bulkActionOptions = [
    { value: 'update_bid', label: 'Update Bid Amount' },
    { value: 'update_budget', label: 'Update Daily Budget' },
    { value: 'pause_profiles', label: 'Pause Profiles' },
    { value: 'activate_profiles', label: 'Activate Profiles' },
    { value: 'apply_template', label: 'Apply Template' }
  ];

  const templateOptions = [
    { value: 'aggressive', label: 'Aggressive Bidding Template' },
    { value: 'conservative', label: 'Conservative Bidding Template' },
    { value: 'balanced', label: 'Balanced Strategy Template' },
    { value: 'weekend', label: 'Weekend Hours Template' }
  ];

  const handleProfileSelection = (profileId, checked) => {
    if (checked) {
      setSelectedProfiles([...selectedProfiles, profileId]);
    } else {
      setSelectedProfiles(selectedProfiles?.filter(id => id !== profileId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProfiles(buyerProfiles?.map(profile => profile?.id));
    } else {
      setSelectedProfiles([]);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedProfiles?.length === 0 || !bulkAction) return;

    setIsProcessing(true);
    
    // Simulate processing time
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const updateData = {
      profileIds: selectedProfiles,
      action: bulkAction,
      value: bulkValue,
      timestamp: new Date()
    };

    onBulkUpdate(updateData);
    setIsProcessing(false);
    setSelectedProfiles([]);
    setBulkAction('');
    setBulkValue('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'paused':
        return 'text-warning bg-warning/10';
      case 'inactive':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const renderValueInput = () => {
    switch (bulkAction) {
      case 'update_bid':
        return (
          <Input
            label="New Bid Amount ($)"
            type="number"
            step="0.25"
            min="0"
            placeholder="Enter bid amount"
            value={bulkValue}
            onChange={(e) => setBulkValue(e?.target?.value)}
          />
        );
      case 'update_budget':
        return (
          <Input
            label="New Daily Budget ($)"
            type="number"
            min="0"
            placeholder="Enter daily budget"
            value={bulkValue}
            onChange={(e) => setBulkValue(e?.target?.value)}
          />
        );
      case 'apply_template':
        return (
          <Select
            label="Select Template"
            options={templateOptions}
            value={bulkValue}
            onChange={setBulkValue}
            placeholder="Choose a template"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Layers" size={20} className="text-secondary" />
          <h3 className="text-lg font-semibold text-foreground">Bulk Bid Management</h3>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">
            {selectedProfiles?.length} of {buyerProfiles?.length} selected
          </span>
          <Button
            variant="outline"
            size="sm"
            iconName="Save"
            onClick={() => onTemplateCreate(selectedProfiles)}
            disabled={selectedProfiles?.length === 0}
          >
            Save as Template
          </Button>
        </div>
      </div>
      {/* Bulk Actions */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Bulk Action"
            options={bulkActionOptions}
            value={bulkAction}
            onChange={setBulkAction}
            placeholder="Select action"
          />
          
          <div className="md:col-span-1">
            {renderValueInput()}
          </div>

          <div className="flex items-end">
            <Button
              variant="default"
              onClick={handleBulkUpdate}
              disabled={selectedProfiles?.length === 0 || !bulkAction || isProcessing}
              loading={isProcessing}
              iconName="Zap"
              className="w-full"
            >
              Apply to Selected
            </Button>
          </div>
        </div>
      </div>
      {/* Profile Selection */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <Checkbox
            label="Select All Profiles"
            checked={selectedProfiles?.length === buyerProfiles?.length}
            indeterminate={selectedProfiles?.length > 0 && selectedProfiles?.length < buyerProfiles?.length}
            onChange={(e) => handleSelectAll(e?.target?.checked)}
          />
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Filter" size={16} />
            <span>Filter by status or type</span>
          </div>
        </div>

        {/* Profiles Grid */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {buyerProfiles?.map((profile) => (
            <div
              key={profile?.id}
              className={`p-4 border rounded-lg transition-smooth ${
                selectedProfiles?.includes(profile?.id)
                  ? 'border-primary bg-primary/5' :'border-border hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    checked={selectedProfiles?.includes(profile?.id)}
                    onChange={(e) => handleProfileSelection(profile?.id, e?.target?.checked)}
                  />
                  <div>
                    <h4 className="font-medium text-foreground">{profile?.name}</h4>
                    <p className="text-sm text-muted-foreground">{profile?.type}</p>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(profile?.status)}`}>
                  {profile?.status?.toUpperCase()}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 pt-3 border-t border-border">
                <div>
                  <div className="text-sm font-medium text-foreground">${profile?.currentBid}</div>
                  <div className="text-xs text-muted-foreground">Current Bid</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">${profile?.dailyBudget}</div>
                  <div className="text-xs text-muted-foreground">Daily Budget</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">{profile?.winRate}%</div>
                  <div className="text-xs text-muted-foreground">Win Rate</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">{profile?.leadsToday}</div>
                  <div className="text-xs text-muted-foreground">Leads Today</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Processing Status */}
      {isProcessing && (
        <div className="p-4 border-t border-border bg-accent/5">
          <div className="flex items-center space-x-2">
            <Icon name="Loader2" size={16} className="text-accent animate-spin" />
            <span className="text-sm text-accent">Processing bulk update...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkBidManagement;